'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import HealthBadge from '@/components/ui/HealthBadge';

const BVOKPIChart = dynamic(() => import('./BVOKPIChart'), { ssr: false });

type HealthStatus = 'healthy' | 'at-risk' | 'critical' | 'recovering' | 'optimized';

interface BVO {
  id: string;
  name: string;
  type: string;
  healthScore: number;
  conformance: number;
  cycleTimeP95: string;
  valueAtRisk: string;
  riskLevel: string;
  status: HealthStatus;
  owner: string;
  activeCases: number;
  openRisks: number;
  lastUpdated: string;
  bvsCount: number;
}

const tabs = ['Overview', 'KPIs', 'Process Insights', 'Risks', 'Recommendations'];

const recommendations: Record<string, string[]> = {
  'bvo-003': [
    'Investigate 847 orders stuck in "Awaiting Stock Allocation" for >3 days',
    'Reduce manual approval steps in fulfillment variant B (affects 31% of cases)',
    'Enable automated SLA escalation for orders exceeding 6-day threshold',
  ],
  'bvo-006': [
    'Accelerate cash collection on 94 overdue receivables ($8.7M exposure)',
    'Review payment terms with top 5 customers contributing to DSO increase',
    'Consider dynamic discounting for early payment incentives',
  ],
};

export default function BVODetailPanel({ bvo, onClose }: { bvo: BVO; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState('Overview');

  const risks = [
    { id: `risk-${bvo.id}-1`, severity: 'high', description: 'Cycle time exceeding SLA threshold in 23% of cases', detected: '2h ago' },
    { id: `risk-${bvo.id}-2`, severity: 'medium', description: 'Conformance rate declining over 7-day trend', detected: '1d ago' },
    { id: `risk-${bvo.id}-3`, severity: 'low', description: 'Variant proliferation detected — 4 new paths this week', detected: '3d ago' },
  ].slice(0, bvo.openRisks || 1);

  const recs = recommendations[bvo.id] || [
    'Review process conformance rules and update ideal path definition',
    'Analyze top 3 variants for automation opportunities',
    'Schedule monthly BVO health review with process owner',
  ];

  return (
    <div className="bg-card rounded-xl card-glow sticky top-[88px] max-h-[calc(100vh-120px)] overflow-hidden flex flex-col animate-fade-in">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{bvo.type} · BVO</p>
            <h2 className="text-base font-semibold text-foreground">{bvo.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <HealthBadge status={bvo.status} />
          <span className="text-xs text-muted-foreground">Health: <strong className="text-foreground font-tabular">{bvo.healthScore}/100</strong></span>
          <span className="text-xs text-muted-foreground">Owner: <strong className="text-foreground">{bvo.owner.split(' ')[0]}</strong></span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={`detail-tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 text-xs font-medium whitespace-nowrap transition-colors border-b-2 ${
              activeTab === tab
                ? 'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {activeTab === 'Overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Health Score', value: `${bvo.healthScore}/100`, color: bvo.healthScore >= 80 ? 'text-positive' : bvo.healthScore >= 60 ? 'text-warning' : 'text-negative' },
                { label: 'Conformance', value: `${bvo.conformance}%`, color: bvo.conformance >= 85 ? 'text-positive' : bvo.conformance >= 70 ? 'text-warning' : 'text-negative' },
                { label: 'Cycle Time P95', value: bvo.cycleTimeP95, color: 'text-foreground' },
                { label: 'Active Cases', value: bvo.activeCases.toLocaleString(), color: 'text-foreground' },
                { label: 'Value at Risk', value: bvo.valueAtRisk, color: bvo.valueAtRisk === '$0' ? 'text-positive' : 'text-negative' },
                { label: 'Open Risks', value: String(bvo.openRisks), color: bvo.openRisks === 0 ? 'text-positive' : 'text-negative' },
              ].map((item) => (
                <div key={`overview-${item.label}`} className="bg-muted/50 rounded-lg p-3">
                  <p className="text-2xs text-muted-foreground mb-1">{item.label}</p>
                  <p className={`text-sm font-bold font-tabular ${item.color}`}>{item.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground mb-2">Linked Business Value Services</p>
              <div className="flex flex-wrap gap-1.5">
                {['Discovery', 'Monitoring', 'Conformance', 'Prediction', 'Root Cause', 'Simulation', 'KPI', 'Risk'].slice(0, bvo.bvsCount).map((svc) => (
                  <span key={`bvs-${svc}`} className="text-2xs bg-accent/15 text-accent px-2 py-0.5 rounded border border-accent/20">
                    {svc} BVS
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'KPIs' && (
          <div className="space-y-4">
            <BVOKPIChart bvoId={bvo.id} />
          </div>
        )}

        {activeTab === 'Process Insights' && (
          <div className="space-y-3">
            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground mb-3">Top Process Variants</p>
              {[
                { variant: 'Happy Path', frequency: 63, conformance: 100 },
                { variant: 'Manual Override', frequency: 18, conformance: 42 },
                { variant: 'Exception Loop', frequency: 11, conformance: 28 },
                { variant: 'Expedited', frequency: 8, conformance: 71 },
              ].map((v) => (
                <div key={`variant-${v.variant}`} className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-foreground">{v.variant}</span>
                    <span className="text-xs font-tabular text-muted-foreground">{v.frequency}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${v.frequency}%`,
                        background: v.conformance >= 80 ? 'var(--positive)' : v.conformance >= 50 ? 'var(--warning)' : 'var(--negative)',
                      }}
                    />
                  </div>
                  <p className="text-2xs text-muted-foreground mt-0.5">Conformance: {v.conformance}%</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'Risks' && (
          <div className="space-y-3">
            {risks.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="ShieldCheckIcon" size={28} className="text-positive mx-auto mb-2" />
                <p className="text-sm text-foreground font-medium">No active risks</p>
                <p className="text-xs text-muted-foreground">This BVO is operating within all defined thresholds</p>
              </div>
            ) : (
              risks.map((risk) => (
                <div key={risk.id} className={`rounded-lg p-4 border ${
                  risk.severity === 'high' ? 'bg-negative/10 border-negative/20' :
                  risk.severity === 'medium'? 'bg-warning/10 border-warning/20' : 'bg-primary/10 border-primary/20'
                }`}>
                  <div className="flex items-start gap-2">
                    <Icon
                      name={risk.severity === 'high' ? 'ExclamationCircleIcon' : 'ExclamationTriangleIcon'}
                      size={14}
                      className={risk.severity === 'high' ? 'text-negative' : risk.severity === 'medium' ? 'text-warning' : 'text-primary'}
                    />
                    <div className="flex-1">
                      <p className="text-xs text-foreground">{risk.description}</p>
                      <p className="text-2xs text-muted-foreground mt-1">Detected {risk.detected}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'Recommendations' && (
          <div className="space-y-3">
            {recs.map((rec, i) => (
              <div key={`rec-${bvo.id}-${i}`} className="flex items-start gap-3 bg-accent/5 border border-accent/20 rounded-lg p-4">
                <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-2xs font-bold text-accent">{i + 1}</span>
                </div>
                <p className="text-xs text-foreground leading-relaxed">{rec}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}